import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Paper,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Slider,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CreateIcon from '@mui/icons-material/Create';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/providers/AuthProvider';
import type {
  GradeLevel,
  QuestionType,
  RubricCriterion,
  EssayConfig,
  EssayDimension,
  EssayRequirements,
} from '@/types/grading';
import {
  DEFAULT_ESSAY_DIMENSIONS,
  DEFAULT_ESSAY_BANDS,
} from '@/types/grading';
import { getPresetRubric } from '@/data/examPresetRubrics';

const gradeLevels: GradeLevel[] = ['P1', 'P2', 'P3', 'P4', 'P5', 'P6', 'S1', 'S2', 'S3', 'S4', 'S5', 'S6'];

const questionTypes: { value: QuestionType; label: string }[] = [
  { value: 'mixed', label: '混合題型' },
  { value: 'essay', label: '作文' },
  { value: 'short_answer', label: '短答題' },
  { value: 'reading_comprehension', label: '閱讀理解' },
  { value: 'fill_in_blank', label: '填充題' },
  { value: 'multiple_choice', label: '選擇題' },
  { value: 'dictation', label: '默書' },
];

// Essay type templates
const essayTemplates = [
  { value: 'narrative', label: '記敘文', description: '記錄事件、描述經歷' },
  { value: 'expository', label: '說明文', description: '解釋事物、說明道理' },
  { value: 'argumentative', label: '議論文', description: '論述觀點、提出論據' },
  { value: 'descriptive', label: '描寫文', description: '描繪事物、刻畫形象' },
  { value: 'expressive', label: '抒情文', description: '表達情感、抒發心情' },
  { value: 'custom', label: '自訂', description: '自行設定評分標準' },
];

// Exam board presets with marking criteria
interface ExamPreset {
  value: string;
  label: string;
  category: string;
  description: string;
  totalScore: number;
  dimensions: {
    content: { weight: number; maxScore: number; name: string };
    organization: { weight: number; maxScore: number; name: string };
    language: { weight: number; maxScore: number; name: string };
    mechanics: { weight: number; maxScore: number; name: string };
  };
}

const examPresets: ExamPreset[] = [
  // GCSE (UK)
  {
    value: 'aqa_gcse',
    label: 'AQA GCSE',
    category: 'GCSE (UK)',
    description: 'AQA GCSE English Language / Chinese',
    totalScore: 40,
    dimensions: {
      content: { weight: 35, maxScore: 14, name: 'Content & Ideas' },
      organization: { weight: 25, maxScore: 10, name: 'Organisation' },
      language: { weight: 25, maxScore: 10, name: 'Vocabulary & Expression' },
      mechanics: { weight: 15, maxScore: 6, name: 'SPaG (Spelling, Punctuation, Grammar)' },
    },
  },
  {
    value: 'edexcel_gcse',
    label: 'Edexcel GCSE',
    category: 'GCSE (UK)',
    description: 'Pearson Edexcel GCSE Chinese',
    totalScore: 40,
    dimensions: {
      content: { weight: 40, maxScore: 16, name: 'Content' },
      organization: { weight: 20, maxScore: 8, name: 'Organisation & Coherence' },
      language: { weight: 30, maxScore: 12, name: 'Range & Accuracy of Language' },
      mechanics: { weight: 10, maxScore: 4, name: 'Accuracy' },
    },
  },
  // iGCSE (International)
  {
    value: 'cambridge_igcse',
    label: 'Cambridge iGCSE',
    category: 'iGCSE (International)',
    description: 'Cambridge International IGCSE Chinese (0547)',
    totalScore: 40,
    dimensions: {
      content: { weight: 35, maxScore: 14, name: 'Content & Relevance' },
      organization: { weight: 25, maxScore: 10, name: 'Structure & Cohesion' },
      language: { weight: 30, maxScore: 12, name: 'Range & Accuracy' },
      mechanics: { weight: 10, maxScore: 4, name: 'Accuracy of Characters' },
    },
  },
  {
    value: 'edexcel_igcse',
    label: 'Edexcel iGCSE',
    category: 'iGCSE (International)',
    description: 'Pearson Edexcel International GCSE Chinese',
    totalScore: 40,
    dimensions: {
      content: { weight: 40, maxScore: 16, name: 'Communication & Content' },
      organization: { weight: 20, maxScore: 8, name: 'Organisation' },
      language: { weight: 30, maxScore: 12, name: 'Linguistic Knowledge' },
      mechanics: { weight: 10, maxScore: 4, name: 'Accuracy' },
    },
  },
  // DSE (Hong Kong)
  {
    value: 'dse_chinese',
    label: 'DSE 中文作文',
    category: 'DSE (香港)',
    description: '香港中學文憑考試 中國語文科 寫作能力',
    totalScore: 105,
    dimensions: {
      content: { weight: 40, maxScore: 42, name: '內容' },
      organization: { weight: 25, maxScore: 27, name: '結構' },
      language: { weight: 25, maxScore: 27, name: '表達' },
      mechanics: { weight: 10, maxScore: 9, name: '字詞標點' },
    },
  },
  // TSA (Hong Kong)
  {
    value: 'tsa_p3',
    label: 'TSA 小三',
    category: 'TSA (香港)',
    description: '全港性系統評估 小學三年級 中文寫作',
    totalScore: 24,
    dimensions: {
      content: { weight: 40, maxScore: 10, name: '內容' },
      organization: { weight: 25, maxScore: 6, name: '結構' },
      language: { weight: 25, maxScore: 6, name: '表達' },
      mechanics: { weight: 10, maxScore: 2, name: '標點字詞' },
    },
  },
  {
    value: 'tsa_p6',
    label: 'TSA 小六',
    category: 'TSA (香港)',
    description: '全港性系統評估 小學六年級 中文寫作',
    totalScore: 36,
    dimensions: {
      content: { weight: 40, maxScore: 15, name: '內容' },
      organization: { weight: 25, maxScore: 9, name: '結構' },
      language: { weight: 25, maxScore: 9, name: '表達' },
      mechanics: { weight: 10, maxScore: 3, name: '標點字詞' },
    },
  },
  {
    value: 'tsa_s3',
    label: 'TSA 中三',
    category: 'TSA (香港)',
    description: '全港性系統評估 中學三年級 中文寫作',
    totalScore: 42,
    dimensions: {
      content: { weight: 40, maxScore: 17, name: '內容' },
      organization: { weight: 25, maxScore: 10, name: '結構' },
      language: { weight: 25, maxScore: 11, name: '表達' },
      mechanics: { weight: 10, maxScore: 4, name: '標點字詞' },
    },
  },
];

// Group presets by category
const presetCategories = examPresets.reduce((acc, preset) => {
  if (!acc[preset.category]) acc[preset.category] = [];
  const categoryArray = acc[preset.category];
  if (categoryArray) {
    categoryArray.push(preset);
  }
  return acc;
}, {} as Record<string, ExamPreset[]>);

function generateId() {
  return Math.random().toString(36).substring(2, 9);
}

const defaultCriteria: RubricCriterion[] = [
  { id: generateId(), name: '內容', description: '答案內容是否正確、完整', maxScore: 40 },
  { id: generateId(), name: '表達', description: '語言表達是否清晰、流暢', maxScore: 30 },
  { id: generateId(), name: '字詞', description: '用字是否正確、恰當', maxScore: 30 },
];

// Dimension editor component
function DimensionEditor({
  dimension,
  onChange,
  totalWeight,
}: {
  dimension: EssayDimension;
  onChange: (d: EssayDimension) => void;
  totalWeight: number;
}) {
  const isDseMechanics = dimension.name.includes('錯別字');
  
  return (
    <Accordion defaultExpanded={false}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
          <Box>
            <Typography fontWeight="bold">{dimension.name}</Typography>
            {isDseMechanics && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                此維度包含：標點字體10分 + 錯別字3分 = 13分
              </Typography>
            )}
          </Box>
          <Chip
            label={`${dimension.weight}%`}
            size="small"
            color={totalWeight === 100 ? 'success' : 'warning'}
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            滿分：{dimension.maxScore}分
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              比重 (%)
            </Typography>
            <Slider
              value={dimension.weight}
              onChange={(_, v) => onChange({ ...dimension, weight: v as number, maxScore: v as number })}
              min={5}
              max={60}
              step={5}
              valueLabelDisplay="auto"
              marks={[
                { value: 10, label: '10%' },
                { value: 25, label: '25%' },
                { value: 40, label: '40%' },
              ]}
            />
          </Box>
        </Box>

        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          等級描述（供AI評分參考）
        </Typography>
        
        <Box sx={{ display: 'grid', gap: 1.5 }}>
          <TextField
            label="優秀 (85-100分)"
            value={dimension.bandDescriptors.excellent}
            onChange={(e) =>
              onChange({
                ...dimension,
                bandDescriptors: { ...dimension.bandDescriptors, excellent: e.target.value },
              })
            }
            multiline
            rows={2}
            size="small"
            fullWidth
          />
          <TextField
            label="良好 (70-84分)"
            value={dimension.bandDescriptors.good}
            onChange={(e) =>
              onChange({
                ...dimension,
                bandDescriptors: { ...dimension.bandDescriptors, good: e.target.value },
              })
            }
            multiline
            rows={2}
            size="small"
            fullWidth
          />
          <TextField
            label="尚可 (50-69分)"
            value={dimension.bandDescriptors.fair}
            onChange={(e) =>
              onChange({
                ...dimension,
                bandDescriptors: { ...dimension.bandDescriptors, fair: e.target.value },
              })
            }
            multiline
            rows={2}
            size="small"
            fullWidth
          />
          <TextField
            label="待改進 (0-49分)"
            value={dimension.bandDescriptors.needsImprovement}
            onChange={(e) =>
              onChange({
                ...dimension,
                bandDescriptors: { ...dimension.bandDescriptors, needsImprovement: e.target.value },
              })
            }
            multiline
            rows={2}
            size="small"
            fullWidth
          />
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

export function NewRubricPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [gradeLevel, setGradeLevel] = useState<GradeLevel>('P4');
  const [questionType, setQuestionType] = useState<QuestionType>('mixed');
  const [criteria, setCriteria] = useState<RubricCriterion[]>(defaultCriteria);
  const [modelAnswer, setModelAnswer] = useState('');
  const [keywords, setKeywords] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Essay-specific state
  const [examPreset, setExamPreset] = useState<string>('');
  const [essayTemplate, setEssayTemplate] = useState('narrative');
  const [essayPrompt, setEssayPrompt] = useState('');
  const [essayDimensions, setEssayDimensions] = useState(DEFAULT_ESSAY_DIMENSIONS);
  const [essayRequirements, setEssayRequirements] = useState<EssayRequirements>({
    minWordCount: undefined,
    maxWordCount: undefined,
    requiredElements: [],
  });
  const [requiredElementInput, setRequiredElementInput] = useState('');

  const isEssay = questionType === 'essay';
  const totalScore = isEssay
    ? essayDimensions.content.maxScore +
      essayDimensions.organization.maxScore +
      essayDimensions.language.maxScore +
      essayDimensions.mechanics.maxScore
    : criteria.reduce((sum, c) => sum + c.maxScore, 0);

  const totalWeight = isEssay
    ? essayDimensions.content.weight +
      essayDimensions.organization.weight +
      essayDimensions.language.weight +
      essayDimensions.mechanics.weight
    : 100;

  // Update dimensions when exam preset changes
  useEffect(() => {
    if (!examPreset) return;
    
    const presetRubric = getPresetRubric(examPreset);
    if (!presetRubric) {
      // Fallback to old behavior if preset not found
      const preset = examPresets.find(p => p.value === examPreset);
      if (!preset) return;
      
      setEssayDimensions({
        content: {
          ...DEFAULT_ESSAY_DIMENSIONS.content,
          name: preset.dimensions.content.name,
          weight: preset.dimensions.content.weight,
          maxScore: preset.dimensions.content.maxScore,
        },
        organization: {
          ...DEFAULT_ESSAY_DIMENSIONS.organization,
          name: preset.dimensions.organization.name,
          weight: preset.dimensions.organization.weight,
          maxScore: preset.dimensions.organization.maxScore,
        },
        language: {
          ...DEFAULT_ESSAY_DIMENSIONS.language,
          name: preset.dimensions.language.name,
          weight: preset.dimensions.language.weight,
          maxScore: preset.dimensions.language.maxScore,
        },
        mechanics: {
          ...DEFAULT_ESSAY_DIMENSIONS.mechanics,
          name: preset.dimensions.mechanics.name,
          weight: preset.dimensions.mechanics.weight,
          maxScore: preset.dimensions.mechanics.maxScore,
        },
      });
      
      if (!name) {
        setName(`${preset.label} 作文評分準則`);
      }
      return;
    }
    
    // Load full preset rubric with band descriptors
    const fullRubric = presetRubric.essayConfig;
    setEssayDimensions(fullRubric.dimensions);
    
    // Auto-fill the rubric name if empty
    if (!name) {
      setName(`${presetRubric.label} 作文評分準則`);
    }
    
    // Optionally set grade level if preset has one
    if (presetRubric.gradeLevel) {
      setGradeLevel(presetRubric.gradeLevel as GradeLevel);
    }
  }, [examPreset]);

  // Update dimensions when template changes
  useEffect(() => {
    if (examPreset) return; // Don't override if using exam preset
    if (essayTemplate === 'custom') return;
    
    // Different templates may have different weight distributions
    const templateConfigs: Record<string, typeof DEFAULT_ESSAY_DIMENSIONS> = {
      narrative: {
        ...DEFAULT_ESSAY_DIMENSIONS,
        content: { ...DEFAULT_ESSAY_DIMENSIONS.content, weight: 40, maxScore: 40 },
        organization: { ...DEFAULT_ESSAY_DIMENSIONS.organization, weight: 25, maxScore: 25 },
        language: { ...DEFAULT_ESSAY_DIMENSIONS.language, weight: 25, maxScore: 25 },
        mechanics: { ...DEFAULT_ESSAY_DIMENSIONS.mechanics, weight: 10, maxScore: 10 },
      },
      expository: {
        ...DEFAULT_ESSAY_DIMENSIONS,
        content: {
          ...DEFAULT_ESSAY_DIMENSIONS.content,
          weight: 35,
          maxScore: 35,
          bandDescriptors: {
            ...DEFAULT_ESSAY_DIMENSIONS.content.bandDescriptors,
            excellent: '解釋清晰準確，條理分明，深入淺出，舉例恰當',
            good: '解釋較為清楚，有條理，能舉例說明',
          },
        },
        organization: {
          ...DEFAULT_ESSAY_DIMENSIONS.organization,
          weight: 30,
          maxScore: 30,
          bandDescriptors: {
            ...DEFAULT_ESSAY_DIMENSIONS.organization.bandDescriptors,
            excellent: '層次清晰，由淺入深，過渡自然，邏輯嚴密',
          },
        },
        language: { ...DEFAULT_ESSAY_DIMENSIONS.language, weight: 25, maxScore: 25 },
        mechanics: { ...DEFAULT_ESSAY_DIMENSIONS.mechanics, weight: 10, maxScore: 10 },
      },
      argumentative: {
        ...DEFAULT_ESSAY_DIMENSIONS,
        content: {
          ...DEFAULT_ESSAY_DIMENSIONS.content,
          weight: 40,
          maxScore: 40,
          bandDescriptors: {
            ...DEFAULT_ESSAY_DIMENSIONS.content.bandDescriptors,
            excellent: '論點鮮明有力，論據充分有說服力，論證嚴密',
            good: '論點明確，論據較充分，論證較有條理',
          },
        },
        organization: {
          ...DEFAULT_ESSAY_DIMENSIONS.organization,
          weight: 30,
          maxScore: 30,
          bandDescriptors: {
            ...DEFAULT_ESSAY_DIMENSIONS.organization.bandDescriptors,
            excellent: '結構完整，層次分明，論證過程清晰，首尾呼應',
          },
        },
        language: { ...DEFAULT_ESSAY_DIMENSIONS.language, weight: 20, maxScore: 20 },
        mechanics: { ...DEFAULT_ESSAY_DIMENSIONS.mechanics, weight: 10, maxScore: 10 },
      },
      descriptive: {
        ...DEFAULT_ESSAY_DIMENSIONS,
        content: { ...DEFAULT_ESSAY_DIMENSIONS.content, weight: 35, maxScore: 35 },
        organization: { ...DEFAULT_ESSAY_DIMENSIONS.organization, weight: 20, maxScore: 20 },
        language: {
          ...DEFAULT_ESSAY_DIMENSIONS.language,
          weight: 35,
          maxScore: 35,
          bandDescriptors: {
            ...DEFAULT_ESSAY_DIMENSIONS.language.bandDescriptors,
            excellent: '描寫生動細膩，善用感官描寫，修辭運用出色，語言優美',
            good: '描寫較為具體，能運用感官描寫和修辭手法',
          },
        },
        mechanics: { ...DEFAULT_ESSAY_DIMENSIONS.mechanics, weight: 10, maxScore: 10 },
      },
      expressive: {
        ...DEFAULT_ESSAY_DIMENSIONS,
        content: {
          ...DEFAULT_ESSAY_DIMENSIONS.content,
          weight: 40,
          maxScore: 40,
          bandDescriptors: {
            ...DEFAULT_ESSAY_DIMENSIONS.content.bandDescriptors,
            excellent: '情感真摯動人，意境深遠，感染力強',
            good: '情感真實，有一定感染力',
          },
        },
        organization: { ...DEFAULT_ESSAY_DIMENSIONS.organization, weight: 20, maxScore: 20 },
        language: {
          ...DEFAULT_ESSAY_DIMENSIONS.language,
          weight: 30,
          maxScore: 30,
          bandDescriptors: {
            ...DEFAULT_ESSAY_DIMENSIONS.language.bandDescriptors,
            excellent: '語言優美感人，善用修辭，富有詩意和感染力',
          },
        },
        mechanics: { ...DEFAULT_ESSAY_DIMENSIONS.mechanics, weight: 10, maxScore: 10 },
      },
    };

    setEssayDimensions(templateConfigs[essayTemplate] || DEFAULT_ESSAY_DIMENSIONS);
  }, [essayTemplate]);

  const addCriterion = () => {
    setCriteria([
      ...criteria,
      { id: generateId(), name: '', description: '', maxScore: 10 },
    ]);
  };

  const removeCriterion = (id: string) => {
    if (criteria.length <= 1) return;
    setCriteria(criteria.filter((c) => c.id !== id));
  };

  const updateCriterion = (id: string, field: keyof RubricCriterion, value: string | number) => {
    setCriteria(
      criteria.map((c) => (c.id === id ? { ...c, [field]: value } : c))
    );
  };

  const updateDimension = (key: keyof typeof essayDimensions, dimension: EssayDimension) => {
    setEssayDimensions((prev) => ({ ...prev, [key]: dimension }));
  };

  const addRequiredElement = () => {
    if (!requiredElementInput.trim()) return;
    setEssayRequirements((prev) => ({
      ...prev,
      requiredElements: [...(prev.requiredElements || []), requiredElementInput.trim()],
    }));
    setRequiredElementInput('');
  };

  const removeRequiredElement = (element: string) => {
    setEssayRequirements((prev) => ({
      ...prev,
      requiredElements: prev.requiredElements?.filter((e) => e !== element),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('請輸入準則名稱');
      return;
    }

    if (!isEssay && criteria.some((c) => !c.name.trim())) {
      setError('請填寫所有評分項目名稱');
      return;
    }

    if (isEssay && totalWeight !== 100) {
      setError('作文評分維度比重總和必須為100%');
      return;
    }

    if (!user) return;

    setLoading(true);

    // Build essay config if essay type
    // Use preset bands if a preset is selected, otherwise use default bands
    const presetRubric = examPreset ? getPresetRubric(examPreset) : null;
    const bandsToUse = presetRubric?.essayConfig.bands || DEFAULT_ESSAY_BANDS;
    
    const essayConfig: EssayConfig | null = isEssay
      ? {
          dimensions: essayDimensions,
          bands: bandsToUse,
          requirements: essayRequirements,
          prompt: essayPrompt || undefined,
          examPreset: examPreset || undefined,
        }
      : null;

    // For essay type, convert dimensions to criteria format for compatibility
    const finalCriteria = isEssay
      ? [
          { id: 'content', name: essayDimensions.content.name, description: essayDimensions.content.bandDescriptors.excellent, maxScore: essayDimensions.content.maxScore },
          { id: 'organization', name: essayDimensions.organization.name, description: essayDimensions.organization.bandDescriptors.excellent, maxScore: essayDimensions.organization.maxScore },
          { id: 'language', name: essayDimensions.language.name, description: essayDimensions.language.bandDescriptors.excellent, maxScore: essayDimensions.language.maxScore },
          { id: 'mechanics', name: essayDimensions.mechanics.name, description: essayDimensions.mechanics.bandDescriptors.excellent, maxScore: essayDimensions.mechanics.maxScore },
        ]
      : criteria;

    const { error: insertError } = await supabase.from('rubrics').insert({
      teacher_id: user.id,
      name,
      grade_level: gradeLevel,
      question_type: questionType,
      total_score: totalScore,
      criteria: finalCriteria,
      model_answer: modelAnswer || null,
      keywords: keywords ? keywords.split(',').map((k) => k.trim()) : null,
      is_template: false,
      essay_config: essayConfig,
    });

    if (insertError) {
      setError(insertError.message);
      setLoading(false);
      return;
    }

    navigate('/rubrics');
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        新增評分準則
      </Typography>

      <Card>
        <CardContent>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Typography variant="h6" gutterBottom>
              基本資料
            </Typography>
            <TextField
              fullWidth
              label="準則名稱"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              sx={{ mb: 2 }}
              placeholder="例如：四年級作文評分準則"
            />

            <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>年級</InputLabel>
                <Select
                  value={gradeLevel}
                  label="年級"
                  onChange={(e) => setGradeLevel(e.target.value as GradeLevel)}
                >
                  {gradeLevels.map((level) => (
                    <MenuItem key={level} value={level}>
                      {level}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel>題型</InputLabel>
                <Select
                  value={questionType}
                  label="題型"
                  onChange={(e) => setQuestionType(e.target.value as QuestionType)}
                >
                  {questionTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Essay-specific form */}
            {isEssay ? (
              <Box>
                <Divider sx={{ my: 3 }} />
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <CreateIcon color="primary" />
                  <Typography variant="h6">作文評分設定</Typography>
                </Box>

                {/* Exam board presets */}
                <Paper sx={{ p: 2, mb: 3, bgcolor: '#fafafa' }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    📋 選擇考試評分標準（可選）
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    選擇考試局預設評分標準，自動配置評分維度和分數分配
                  </Typography>
                  
                  {Object.entries(presetCategories).map(([category, presets]) => (
                    <Box key={category} sx={{ mb: 2 }}>
                      <Typography variant="caption" fontWeight="bold" color="primary" sx={{ display: 'block', mb: 0.5 }}>
                        {category}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {presets.map((preset) => (
                          <Chip
                            key={preset.value}
                            label={preset.label}
                            onClick={() => {
                              setExamPreset(examPreset === preset.value ? '' : preset.value);
                            }}
                            color={examPreset === preset.value ? 'primary' : 'default'}
                            variant={examPreset === preset.value ? 'filled' : 'outlined'}
                            size="small"
                          />
                        ))}
                      </Box>
                    </Box>
                  ))}
                  
                  {examPreset && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      已選擇：<strong>{examPresets.find(p => p.value === examPreset)?.label}</strong>
                      {' - '}
                      {examPresets.find(p => p.value === examPreset)?.description}
                      {' (總分：'}
                      {examPresets.find(p => p.value === examPreset)?.totalScore}
                      {'分)'}
                    </Alert>
                  )}
                </Paper>

                {/* Essay template selection - only show if no exam preset selected */}
                {!examPreset && (
                  <>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      或選擇文體模板
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
                      {essayTemplates.map((template) => (
                        <Chip
                          key={template.value}
                          label={template.label}
                          onClick={() => setEssayTemplate(template.value)}
                          color={essayTemplate === template.value ? 'primary' : 'default'}
                          variant={essayTemplate === template.value ? 'filled' : 'outlined'}
                        />
                      ))}
                    </Box>
                  </>
                )}

                {/* Essay prompt */}
                <TextField
                  fullWidth
                  label="作文題目/要求"
                  value={essayPrompt}
                  onChange={(e) => setEssayPrompt(e.target.value)}
                  multiline
                  rows={2}
                  sx={{ mb: 3 }}
                  placeholder="例如：記一次難忘的旅行，要求：1. 描述旅途中的見聞和感受 2. 字數不少於300字"
                />

                {/* Dimension editors */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    評分維度
                  </Typography>
                  <Chip
                    label={`總分：${totalScore}分`}
                    color="primary"
                    variant="outlined"
                  />
                </Box>

                {totalWeight !== 100 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    維度比重總和為 {totalWeight}%，需調整至 100%
                  </Alert>
                )}

                <Box sx={{ mb: 3 }}>
                  <DimensionEditor
                    dimension={essayDimensions.content}
                    onChange={(d) => updateDimension('content', d)}
                    totalWeight={totalWeight}
                  />
                  <DimensionEditor
                    dimension={essayDimensions.organization}
                    onChange={(d) => updateDimension('organization', d)}
                    totalWeight={totalWeight}
                  />
                  <DimensionEditor
                    dimension={essayDimensions.language}
                    onChange={(d) => updateDimension('language', d)}
                    totalWeight={totalWeight}
                  />
                  <DimensionEditor
                    dimension={essayDimensions.mechanics}
                    onChange={(d) => updateDimension('mechanics', d)}
                    totalWeight={totalWeight}
                  />
                </Box>

                {/* Requirements */}
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  作文要求（選填）
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <TextField
                    label="最少字數"
                    type="number"
                    value={essayRequirements.minWordCount || ''}
                    onChange={(e) =>
                      setEssayRequirements((prev) => ({
                        ...prev,
                        minWordCount: e.target.value ? parseInt(e.target.value, 10) : undefined,
                      }))
                    }
                    sx={{ width: 150 }}
                    inputProps={{ min: 0 }}
                  />
                  <TextField
                    label="最多字數"
                    type="number"
                    value={essayRequirements.maxWordCount || ''}
                    onChange={(e) =>
                      setEssayRequirements((prev) => ({
                        ...prev,
                        maxWordCount: e.target.value ? parseInt(e.target.value, 10) : undefined,
                      }))
                    }
                    sx={{ width: 150 }}
                    inputProps={{ min: 0 }}
                  />
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    必須包含的寫作元素
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField
                      size="small"
                      value={requiredElementInput}
                      onChange={(e) => setRequiredElementInput(e.target.value)}
                      placeholder="例如：比喻、排比、特定詞語"
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addRequiredElement())}
                    />
                    <Button variant="outlined" onClick={addRequiredElement}>
                      新增
                    </Button>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {essayRequirements.requiredElements?.map((element) => (
                      <Chip
                        key={element}
                        label={element}
                        onDelete={() => removeRequiredElement(element)}
                        size="small"
                      />
                    ))}
                  </Box>
                </Box>
              </Box>
            ) : (
              /* Standard criteria form for non-essay types */
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    評分項目 <Typography component="span" color="text.secondary">(總分：{totalScore})</Typography>
                  </Typography>
                  <Button startIcon={<AddIcon />} onClick={addCriterion}>
                    新增項目
                  </Button>
                </Box>

                {criteria.map((criterion) => (
                  <Paper key={criterion.id} sx={{ p: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                      <TextField
                        label="項目名稱"
                        value={criterion.name}
                        onChange={(e) => updateCriterion(criterion.id, 'name', e.target.value)}
                        required
                        sx={{ width: 150 }}
                        placeholder="例如：內容"
                      />
                      <TextField
                        label="說明"
                        value={criterion.description}
                        onChange={(e) => updateCriterion(criterion.id, 'description', e.target.value)}
                        sx={{ flex: 1 }}
                        placeholder="描述評分標準"
                      />
                      <TextField
                        label="分數"
                        type="number"
                        value={criterion.maxScore}
                        onChange={(e) =>
                          updateCriterion(criterion.id, 'maxScore', parseInt(e.target.value, 10) || 0)
                        }
                        sx={{ width: 100 }}
                        inputProps={{ min: 0 }}
                      />
                      <IconButton
                        onClick={() => removeCriterion(criterion.id)}
                        disabled={criteria.length <= 1}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}

            <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
              參考答案（選填）
            </Typography>
            <TextField
              fullWidth
              label={isEssay ? '範文或寫作指引' : '標準答案或參考內容'}
              multiline
              rows={4}
              value={modelAnswer}
              onChange={(e) => setModelAnswer(e.target.value)}
              sx={{ mb: 2 }}
              placeholder={isEssay ? '填寫優秀範文或寫作指引，AI 會參考此內容進行評分' : '填寫標準答案，AI 會參考此答案進行評分'}
            />
            <TextField
              fullWidth
              label="關鍵詞（以逗號分隔）"
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
              placeholder={isEssay ? '例如：情感真摯, 描寫細膩, 結構完整' : '例如：主題明確, 段落分明, 用詞恰當'}
            />

            <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
              <Button type="submit" variant="contained" size="large" disabled={loading}>
                {loading ? <CircularProgress size={24} /> : '儲存準則'}
              </Button>
              <Button variant="outlined" size="large" onClick={() => navigate('/rubrics')}>
                取消
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
